import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CATEGORY_LABELS,
  School,
  SchoolTree,
  StudentSchoolData,
  fetchSchoolTree,
  findSchoolById,
  findSchoolByName,
  getAvailableCategories,
  getAvailableYears,
  getSchoolTypes,
  getSchools,
  getYearLabel,
} from '../../utils/school';

export interface SchoolDataValue {
  school_id: number | null;
  school_class: string;
  dirty: boolean;
  valid: boolean;
  /** true mentre il wizard è aperto: il chiamante non deve poter confermare. */
  editing: boolean;
}

interface SchoolDataFormProps {
  /** Dati attuali dello studente; vuoto/assente in modalità ricompilazione. */
  initial?: StudentSchoolData | null;
  /** 'summary' mostra un riepilogo modificabile su richiesta, 'wizard' guida passo passo. */
  variant: 'summary' | 'wizard';
  /**
   * Azione finale del chiamante, resa all'ultimo step al posto di "Prosegui":
   * così il wizard ha un solo pulsante primario per volta.
   */
  submit?: { label: string; onClick: () => void; disabled?: boolean };
  onChange: (value: SchoolDataValue) => void;
}

type WizardStep = 'category' | 'type' | 'school' | 'year';

const WIZARD_STEPS: WizardStep[] = ['category', 'type', 'school', 'year'];

const cardClass = (selected: boolean) =>
  `p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
    selected ? 'border-fpcred bg-fpcred bg-opacity-10' : 'border-gray-300 hover:border-fpcred hover:bg-gray-50'
  }`;

const SchoolDataForm: React.FC<SchoolDataFormProps> = ({ initial, variant, submit, onChange }) => {
  const [tree, setTree] = useState<SchoolTree | null>(null);
  const [treeError, setTreeError] = useState(false);
  const [level, setLevel] = useState('');
  const [type, setType] = useState('');
  const [schoolId, setSchoolId] = useState<number | null>(null);
  const [schoolClass, setSchoolClass] = useState('');
  const [editing, setEditing] = useState(variant === 'wizard');
  const [step, setStep] = useState<WizardStep>('category');

  const initialSchoolId = initial?.school_id ?? null;
  const initialSchoolClass = initial?.school_class ? String(initial.school_class) : '';

  useEffect(() => {
    let mounted = true;
    fetchSchoolTree()
      .then((data) => mounted && setTree(data))
      .catch((err) => {
        console.error('Errore nel recupero delle scuole:', err);
        if (mounted) setTreeError(true);
      });
    return () => {
      mounted = false;
    };
  }, []);

  /** Riporta il form ai dati registrati dello studente. */
  const resetToInitial = useCallback(() => {
    if (!tree) return;
    const match =
      findSchoolById(tree, initialSchoolId) || findSchoolByName(tree, initial?.school_name, initial?.school_level);
    if (match) {
      setLevel(match.level);
      setType(match.type);
      setSchoolId(match.school.id);
    } else {
      setSchoolId(null);
      setType('');
      setLevel(initial?.school_level && tree[initial.school_level] ? initial.school_level : '');
    }
    setSchoolClass(initialSchoolClass);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, initialSchoolId, initialSchoolClass, initial?.school_name, initial?.school_level]);

  // Preseleziona i dati esistenti una volta caricato l'albero delle scuole.
  useEffect(() => {
    resetToInitial();
  }, [resetToInitial]);

  // L'id di partenza può non essere noto (il backend espone solo il nome):
  // in quel caso la baseline è l'indirizzo risolto al primo caricamento.
  const [baselineSchoolId, setBaselineSchoolId] = useState<number | null>(null);
  useEffect(() => {
    if (variant === 'summary' && baselineSchoolId === null && schoolId !== null) {
      setBaselineSchoolId(schoolId);
    }
  }, [variant, baselineSchoolId, schoolId]);

  // Con una sola alternativa la scelta è obbligata (medie ed elementari hanno una
  // tipologia e un indirizzo): si compila da sé così lo step corrispondente sparisce.
  useEffect(() => {
    if (!tree || !level) return;
    const types = getSchoolTypes(tree, level);
    if (!type) {
      if (types.length === 1) setType(types[0]);
      return;
    }
    if (schoolId) return;
    const schools = getSchools(tree, level, type);
    if (schools.length === 1) setSchoolId(schools[0].id);
  }, [tree, level, type, schoolId]);

  useEffect(() => {
    const baseline = initialSchoolId ?? baselineSchoolId;
    // Nel riepilogo l'indirizzo resta quello già registrato: basta l'anno.
    // Nel wizard si sta (ri)compilando tutto, quindi serve anche l'indirizzo.
    const valid = editing ? !!schoolId && !!schoolClass : !!schoolClass;
    onChange({
      school_id: schoolId,
      school_class: schoolClass,
      dirty: schoolId !== baseline || schoolClass !== initialSchoolClass,
      valid,
      editing,
    });
    // onChange è ricreato a ogni render dai chiamanti: escluso di proposito dalle dipendenze.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, schoolClass, initialSchoolId, baselineSchoolId, initialSchoolClass, editing]);

  const selectedSchool = useMemo<School | null>(() => findSchoolById(tree, schoolId)?.school || null, [tree, schoolId]);

  const currentYear = Number(initialSchoolClass);
  const canRepeatYear = variant === 'summary' && currentYear > 1;
  const repeatingYear = canRepeatYear && schoolClass === String(currentYear - 1);
  // Il wizard aperto dal riepilogo deve poterci tornare.
  const cameFromSummary = variant === 'summary';

  // In promozione ordinamento e anno non si toccano: l'ordinamento resta quello
  // registrato e l'anno è quello della promozione, correggibile solo col toggle.
  const keepLevelAndYear = cameFromSummary;

  /** Riporta l'anno nell'intervallo valido per l'ordinamento scelto. */
  const clampYear = (nextLevel: string, current: string) => {
    if (!current) return '';
    const years = getAvailableYears(nextLevel);
    if (years.some((y) => y.value === current)) return current;
    return years[years.length - 1]?.value || '';
  };

  // Ri-toccare la scelta già attiva non deve azzerare gli step successivi:
  // il wizard può aprirsi precompilato (modifica dai dati esistenti).
  const handleSelectLevel = (nextLevel: string) => {
    if (nextLevel === level) return;
    setLevel(nextLevel);
    setSchoolId(null);
    setSchoolClass(keepLevelAndYear ? clampYear(nextLevel, schoolClass) : '');
    const types = getSchoolTypes(tree, nextLevel);
    // Salta la scelta della tipologia quando ce n'è una sola.
    setType(types.length === 1 ? types[0] : '');
  };

  const handleSelectType = (nextType: string) => {
    if (nextType === type) return;
    setType(nextType);
    setSchoolId(null);
    if (!keepLevelAndYear) setSchoolClass('');
  };

  const handleSelectSchool = (school: School) => {
    if (school.id === schoolId) return;
    setSchoolId(school.id);
    if (!keepLevelAndYear) setSchoolClass('');
  };

  const startEditing = (firstStep: WizardStep) => {
    setEditing(true);
    setStep(firstStep);
  };

  const cancelEditing = () => {
    resetToInitial();
    setEditing(false);
  };

  if (treeError) {
    return <p className="text-center text-gray-600">Impossibile caricare l'elenco delle scuole. Riprova più tardi.</p>;
  }

  if (!tree) {
    return <p className="text-center text-gray-600">Caricamento dati scuola…</p>;
  }

  const types = getSchoolTypes(tree, level);
  const schools = getSchools(tree, level, type);
  const visibleSteps = WIZARD_STEPS.filter((s) => {
    // In promozione l'ordinamento resta quello registrato e l'anno quello promosso.
    // Se però non sono stati risolti, lo step torna a disposizione per rimediare.
    if (s === 'category') return !keepLevelAndYear || !level;
    if (s === 'year') return !keepLevelAndYear || !schoolClass;
    // Con una sola tipologia disponibile è già auto-selezionata: lo step si salta.
    if (s === 'type') return types.length > 1;
    // Idem con un solo indirizzo: senza tipologia scelta l'elenco è vuoto e lo step resta.
    if (s === 'school') return schools.length !== 1;
    return true;
  });

  /* ---------- Riepilogo (notifica di promozione) ---------- */
  if (!editing) {
    const rows: { label: string; value: string }[] = [
      { label: 'Ordinamento', value: CATEGORY_LABELS[level] || level || '—' },
      { label: 'Tipologia', value: type || '—' },
      { label: 'Indirizzo', value: selectedSchool?.name || initial?.school_name || '—' },
      { label: 'Anno scolastico', value: getYearLabel(schoolClass) || '—' },
    ];

    return (
      <div className="flex flex-col gap-4 w-full">
        <div className="flex flex-col divide-y divide-gray-200 border-2 border-gray-200 rounded-xl">
          {rows.map((row) => (
            <div key={row.label} className="flex items-start justify-between gap-4 p-3">
              <span className="text-gray-500 shrink-0">{row.label}</span>
              <span className="font-semibold capitalize text-right">{row.value}</span>
            </div>
          ))}
        </div>

        {canRepeatYear && (
          <button
            type="button"
            role="switch"
            aria-checked={repeatingYear}
            onClick={() => setSchoolClass(repeatingYear ? initialSchoolClass : String(currentYear - 1))}
            className={`flex items-center justify-between gap-4 p-3 border-2 rounded-xl text-left transition-colors duration-200 ${
              repeatingYear ? 'border-fpcred bg-fpcred bg-opacity-10' : 'border-gray-300 hover:border-fpcred'
            }`}>
            <span className="text-sm">
              È stato bocciato - ripete il {getYearLabel(String(currentYear - 1)).toLowerCase()}
            </span>
            <span
              className={`shrink-0 w-11 h-6 rounded-full p-1 transition-colors duration-200 ${
                repeatingYear ? 'bg-fpcred' : 'bg-gray-300'
              }`}>
              <span
                className={`block w-4 h-4 bg-white rounded-full transition-transform duration-200 ${
                  repeatingYear ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </span>
          </button>
        )}

        {/* Con medie ed elementari non resta nulla da scegliere: ordinamento e anno
            sono quelli della promozione, tipologia e indirizzo sono obbligati. */}
        {visibleSteps.length > 0 && (
          <button
            type="button"
            onClick={() => startEditing(visibleSteps[0])}
            className="self-center px-6 py-2 border-2 border-dashed border-fpcred/40 text-fpcred rounded-lg hover:bg-fpcred/10 transition-colors">
            Modifica informazioni scuola
          </button>
        )}
      </div>
    );
  }

  /* ---------- Wizard (ricompilazione o modifica) ---------- */
  const stepIndex = visibleSteps.indexOf(step);
  const isFirstStep = stepIndex <= 0;
  const isLastStep = stepIndex === visibleSteps.length - 1;
  const canGoNext =
    (step === 'category' && !!level) ||
    (step === 'type' && !!type) ||
    (step === 'school' && !!schoolId) ||
    (step === 'year' && !!schoolClass);
  const canFinish = !!schoolId && !!schoolClass;
  const goToStep = (offset: number) => {
    const next = visibleSteps[stepIndex + offset];
    if (next) setStep(next);
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      {step === 'category' && (
        <div className="flex flex-col gap-3">
          <h4 className="text-md font-semibold text-center">Ordinamento</h4>
          <div className="grid grid-cols-1 gap-3">
            {getAvailableCategories(tree).map((category) => (
              <div key={category} className={cardClass(level === category)} onClick={() => handleSelectLevel(category)}>
                <div className="text-center font-semibold">{CATEGORY_LABELS[category] || category}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 'type' && (
        <div className="flex flex-col gap-3">
          <h4 className="text-md font-semibold text-center">Tipo di scuola</h4>
          <div className="grid grid-cols-1 gap-3">
            {types.map((schoolType) => (
              <div
                key={schoolType}
                className={cardClass(type === schoolType)}
                onClick={() => handleSelectType(schoolType)}>
                <div className="text-center font-semibold capitalize">{schoolType}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 'school' && (
        <div className="flex flex-col gap-3">
          <h4 className="text-md font-semibold text-center">Seleziona l'indirizzo</h4>
          <div className="grid grid-cols-1 gap-3">
            {schools.map((school) => (
              <div
                key={school.id}
                className={cardClass(schoolId === school.id)}
                onClick={() => handleSelectSchool(school)}>
                <div className="text-center font-semibold capitalize">{school.name}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 'year' && (
        <div className="flex flex-col gap-3">
          <h4 className="text-md font-semibold text-center">Seleziona l'anno</h4>
          <div className="grid grid-cols-2 gap-3">
            {getAvailableYears(level).map((year) => (
              <button
                key={year.value}
                type="button"
                className={cardClass(schoolClass === year.value)}
                onClick={() => setSchoolClass(year.value)}>
                <div className="text-sm font-semibold">{year.label}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-center gap-4">
        {isFirstStep ? (
          cameFromSummary && (
            <button
              type="button"
              className="bg-gray-500 hover:bg-gray-600 p-2 w-full rounded-lg text-white font-semibold transition-colors duration-200"
              onClick={cancelEditing}>
              Annulla
            </button>
          )
        ) : (
          <button
            type="button"
            className="bg-gray-500 hover:bg-gray-600 p-2 w-full rounded-lg text-white font-semibold transition-colors duration-200"
            onClick={() => goToStep(-1)}>
            Indietro
          </button>
        )}

        {!isLastStep && (
          <button
            type="button"
            disabled={!canGoNext}
            className={`p-2 w-full rounded-lg text-white font-semibold transition-colors duration-200 ${
              canGoNext ? 'bg-fpcred hover:bg-buttonhover' : 'bg-gray-300 cursor-not-allowed'
            }`}
            onClick={() => goToStep(1)}>
            Prosegui
          </button>
        )}

        {isLastStep && cameFromSummary && (
          <button
            type="button"
            disabled={!canFinish}
            className={`p-2 w-full rounded-lg text-white font-semibold transition-colors duration-200 ${
              canFinish ? 'bg-fpcred hover:bg-buttonhover' : 'bg-gray-300 cursor-not-allowed'
            }`}
            onClick={() => setEditing(false)}>
            Fine
          </button>
        )}

        {isLastStep && !cameFromSummary && submit && (
          <button
            type="button"
            disabled={submit.disabled}
            className={`p-2 w-full rounded-lg text-white font-semibold transition-colors duration-200 ${
              submit.disabled ? 'bg-gray-300 cursor-not-allowed' : 'bg-fpcred hover:bg-buttonhover'
            }`}
            onClick={submit.onClick}>
            {submit.label}
          </button>
        )}
      </div>

      <div className="flex justify-center gap-2">
        {visibleSteps.map((s) => (
          <div
            key={s}
            className={`w-2.5 h-2.5 rounded-full transition-colors duration-200 ${
              s === step ? 'bg-fpcred' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default SchoolDataForm;
